import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from "@angular/material";
import { Inject } from "@angular/core";
import { ApiService } from "../../../services/api.service";
import { AlertService, MessageSeverity } from "../../../services/alert.service";

/**
 * Editor for a benefit's card icon.
 *
 * The icon is a URL held in the feature's DefaultSettings JSON, which admins previously
 * had to reach through the Whitelabels config dialog and edit as raw JSON. Saving here
 * also moves the sites that were still on the old icon forward -- see UpdateIcon on
 * WhitelabelFeatureController for why that is not automatic.
 */
@Component({
    selector: 'benefit-icon-sheet',
    template: require('./benefit-icon-sheet.html'),
    styles: [require('./benefit-icon-sheet.css')],
})
export class BenefitIconSheetComponent implements OnInit {

    static readonly MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

    static readonly ALLOWED_TYPES = [
        'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'
    ];

    whitelabelFeature: any;
    icon = '';
    saving = false;
    uploading = false;

    private originalIcon = '';

    constructor(private changeRef: ChangeDetectorRef,
                private alertService: AlertService,
                private apiService: ApiService,
                @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
                private bottomSheetRef: MatBottomSheetRef<BenefitIconSheetComponent>) {
        this.whitelabelFeature = data.whitelabelFeature;
    }

    ngOnInit() {
        const settings = this.whitelabelFeature.defaultSettingsObj || {};
        this.icon = settings.icon || '';
        this.originalIcon = this.icon;
    }

    get changed(): boolean {
        return (this.icon || '').trim() !== (this.originalIcon || '').trim();
    }

    /** Mirrors the server's check so a bad value is caught before the round trip. */
    get looksValid(): boolean {
        const v = (this.icon || '').trim();
        if (!v) { return true; }
        return /^https?:\/\//i.test(v) || v.indexOf('//') === 0 || v.indexOf('/') === 0 || /^data:image\//i.test(v);
    }

    onFileSelected(event: any) {
        const file = event.target && event.target.files && event.target.files[0];
        if (!file) { return; }

        if (BenefitIconSheetComponent.ALLOWED_TYPES.indexOf(file.type) === -1) {
            this.alertService.showMessage("",
                "Pick a PNG, JPEG, GIF, WebP or SVG image.", MessageSeverity.warn);
            event.target.value = '';
            return;
        }

        if (file.size > BenefitIconSheetComponent.MAX_UPLOAD_BYTES) {
            this.alertService.showMessage("",
                "That image is " + Math.round(file.size / 1024) + " KB. Icons are limited to 2 MB.",
                MessageSeverity.warn);
            event.target.value = '';
            return;
        }

        // Uploaded to blob storage straight away so the preview shows the real hosted
        // image. Nothing is written to the benefit until Save, so an abandoned upload
        // just leaves an unreferenced blob behind.
        this.uploading = true;
        this.apiService.uploadFeatureIcon(this.whitelabelFeature.whitelabelFeatureId, file)
            .subscribe(r => {
                this.uploading = false;
                if (r && r.url) { this.icon = r.url; }
                this.changeRef.detectChanges();
            }, () => {
                this.uploading = false;
                this.changeRef.detectChanges();
            });

        event.target.value = '';
    }

    clear() {
        this.icon = '';
    }

    save() {
        if (!this.looksValid) { return; }

        this.saving = true;
        this.apiService.saveFeatureIcon(this.whitelabelFeature.whitelabelFeatureId, (this.icon || '').trim())
            .subscribe(r => {
                this.saving = false;

                if (!r) { this.changeRef.detectChanges(); return; }

                let message = "Icon updated for " + this.whitelabelFeature.featureName + ".";
                if (r.syncedSites) {
                    message += " " + r.syncedSites + " site" + (r.syncedSites === 1 ? "" : "s") + " updated.";
                }
                if (r.keptCustomSites) {
                    message += " " + r.keptCustomSites + " site" + (r.keptCustomSites === 1 ? "" : "s") +
                               " kept a custom icon set in Whitelabels.";
                }

                this.alertService.showMessage("", message, MessageSeverity.success);
                this.bottomSheetRef.dismiss(true);
            }, () => {
                this.saving = false;
                this.changeRef.detectChanges();
            });
    }

    cancel() {
        this.bottomSheetRef.dismiss();
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/components/benefit-contents/benefit-icon/benefit-icon-sheet.ts